import { createJSONStorage, persist } from 'zustand/middleware';
import { _initialState } from './_initialState';
import {
  addCabinetToDesign,
  createAndUpdateDesign,
  updateCabinetVisualizer,
  updateDesign,
  updateDesignWallSize,
  updateDesignProducts,
  deleteDesign,
  loadProject,
  reloadDesign,
  addDesignToProject,
  recalculateToekicks,
  applyCollectionMapping,
} from './actions/index';
import updateDesignAccessories from './actions/updateDesignAccessories';
import { migrateToProjectStructure, needsMigration } from '../../utils/migrateLocalStorage';
import { TAB_KEYS, setTabState, setTabValue, TAB_STORAGE_MAP } from '../../utils/stateTabUtility';

export const cabinetVisualizerStore = persist(
  (set, get) => ({
    ..._initialState,
    createAndUpdateDesign: (host, designName, extraData, navigate) => createAndUpdateDesign(set,
      get, host, designName, extraData, navigate),
    updateDesign: (host, designId, designData) => updateDesign(set, get, host, designId, designData),
    addCabinetToDesign: (product, designId) => addCabinetToDesign(set, get, product, designId),
    updateDesignWallSize: (designId, width, height) => updateDesignWallSize(set, get, designId, width, height),
    updateCabinetVisualizer: (data) => {
      // Save tab specific updates into tab state for debugging
      // cabinet: 'cabinet-design-session-storage',
      // The tab state will be stored in window.__tabCabinetDesignSessionStorage
      // Look for these window objects in the browser for debugging and inspection.
      const tabUpdates = {};
      TAB_KEYS.forEach((key) => {
        if (key in data) {
          tabUpdates[key] = data[key];
        }
      });
      if (Object.keys(tabUpdates).length > 0) {
        setTabState(tabUpdates, TAB_STORAGE_MAP.cabinet);
      }
      return updateCabinetVisualizer(set, get, data);
    },
    updateDesignProducts: (host, designId, updatedProducts, addedProductInfo, metadataBaseUrl,
      cabinetDirection) => updateDesignProducts(
      set, get, host, designId, updatedProducts, addedProductInfo, metadataBaseUrl, cabinetDirection),
    updateDesignAccessories: (host, designId, updatedAccessories, cabinetInfo) => updateDesignAccessories(set,
      get, host, designId, updatedAccessories, cabinetInfo),
    recalculateToekicks: (host, designs) => recalculateToekicks(set, get, host, designs),
    deleteDesign: (designId, host, navigate) => deleteDesign(set, get, host, designId, navigate),
    loadProject: (projectId, host, options) => loadProject(
      set, get, projectId, host, options
    ),
    reloadDesign: (designId, host, options) => reloadDesign(set, get, designId, host, options),
    addDesignToProject: (projectId, designDocument, host) => addDesignToProject(
      set, get, projectId, designDocument, host),
    applyCollectionMapping: (host, selectedCollection, options) => applyCollectionMapping(
      set, get, host, selectedCollection, options),
    setUserSelectedCollection: (styleChoiceId) => set((state) => {
      setTabValue('userSelectedCollection', styleChoiceId, TAB_STORAGE_MAP.cabinet);
      return {
        cabinetVisualizer: {
          ...state.cabinetVisualizer,
          userSelectedCollection: styleChoiceId
        }
      };
    }),
    setPrevAnalyticsFilterGroups: (groups) => set({ prevAnalyticsFilterGroups: groups }),
    setHasFiredInitialRefinementsEvent: (value) => set({ hasFiredInitialRefinementsEvent: value }),
  }),
  {
    name: 'cabinet-design-session-storage',
    version: 1,
    storage: createJSONStorage(() => localStorage),
    partialize: (state) => {
      const {
        projects,
        _migrationInfo,
      } = state.cabinetVisualizer || {};
      return {
        projects,
        _migrationInfo
      };
    },
    merge: (persistedState, currentState) => {
      // Check if migration is needed
      let migratedState = persistedState;
      if (needsMigration(persistedState)) {

        migratedState = migrateToProjectStructure(persistedState);
      }

      // Check if we have any Shaker designs (styleChoiceId 248904) in the persisted state
      const hasShaker = migratedState?.designs && Object.values(migratedState.designs).some(
        (design) => parseInt(design.DesignStyleChoiceid, 10) === 248904
      );

      // Check for URL parameters - they would be indicated by userSelectedStyleChoice being true
      const userHasSelectedCollection = migratedState?.userSelectedStyleChoice === true;

      // IMPORTANT: Let styleChoiceId be null for true clean URLs to show Select Collection button
      // Only preserve styleChoiceId when user has explicitly selected a collection or has Shaker designs
      let safeStyleChoiceId;

      // A clean URL does NOT have either "DesignStyleChoiceid" or "designid" query parameters
      // It may have other query parameters, but not these specific ones
      // Both /p/cabinet-shop-your-space/ and /p/cabinet-shop-your-space/334219672 are considered clean URLs
      // as long as they don't have these specific query parameters
      const basePathPattern = /^\/p\/cabinet-shop-your-space(\/\d+)?\/?((\?|#).*)?$/;
      const hasSpecificQueryParams = typeof window !== 'undefined'
        && (window.location.search.includes('DesignStyleChoiceid')
        || window.location.search.includes('designid'));
      const isCleanUrl = typeof window !== 'undefined'
      && basePathPattern.test(window.location.pathname) && !hasSpecificQueryParams;

      // If this is a clean URL or we have no designs at all, show the collection button
      if (isCleanUrl || !migratedState?.designIds || migratedState.designIds.length === 0) {
        safeStyleChoiceId = null; // Show collection button
      } else if (userHasSelectedCollection) {
        // If user has explicitly selected a collection, respect that choice
        safeStyleChoiceId = migratedState?.styleChoiceId || 248904;
      } else {
        // For all other cases, preserve existing value or default to Shaker if needed
        safeStyleChoiceId = migratedState?.styleChoiceId;
      }

      return {
        ...currentState,
        cabinetVisualizer: {
          ...currentState.cabinetVisualizer,
          designs: migratedState?.designs || {},
          designIds: migratedState?.designIds || [],
          selectedDesignId: migratedState?.selectedDesignId,
          styleChoiceId: parseInt(safeStyleChoiceId, 10),
          // Project-based fields
          projects: migratedState?.projects || {},
          currentProjectId: migratedState?.currentProjectId || null,
          _migrationInfo: migratedState?._migrationInfo || null,
          // Always have a default style ID for temp designs
          defaultStyleChoiceId: 248904,
          // Preserve user selection state
          userSelectedStyleChoice: migratedState?.userSelectedStyleChoice || false,
          // Landing page flag - only true if we have no designs AND no selected collection
          isLandingPage: (!migratedState?.designIds || migratedState.designIds.length === 0)
          && !userHasSelectedCollection
        }
      };
    }
  }
);
